import { z } from 'zod/v4';

import { EntropyAuditSchema } from './audit.schema';
import { SpreadPositionSchema } from './spreads.schema';
import {
  ElementSchema,
  OrientationSchema,
  ReadingModeSchema,
  SephirahNameSchema,
  SuitSchema,
  TarotCardSchema,
} from './tarot.schema';

export const InteractionTypeSchema = z.enum([
  'HARMONIC_RESONANCE',
  'TEMPORAL_FLOW',
  'ARCHETYPAL_COLLISION',
  'DYNAMIC_TENSION',
  'ELEMENTAL_HARMONY',
  'QLIPHOTIC_SHADOW',
  'SEPHIROTIC_BRIDGE',
  'DECANAL_SEQUENCE',
  'ANGELIC_PAIR',
  'SUIT_REINFORCEMENT',
  'COURT_DYNAMICS',
  'MIRROR_OPPOSITION',
  'NEUTRAL_DIALOGUE',
]);

export const DrawnCardSchema = TarotCardSchema.extend({
  orientation: OrientationSchema,
  position: SpreadPositionSchema,
  draw_index: z.number().int().nonnegative(),
});

export const CardInterpretationSchema = z.object({
  card: DrawnCardSchema,
  position_meaning: z.string().min(1),
  keywords_active: z.array(z.string().min(1)),
  elemental_note: z.string().min(1).nullable(),
  kabbalistic_note: z.string().min(1).nullable(),
  qliphotic_note: z.string().min(1).nullable(),
  lilith_note: z.string().min(1).nullable(),
  dignity_note: z.string().min(1).nullable(),
  decan_note: z.string().min(1).nullable(),
  temporal_note: z.string().min(1).nullable(),
});

export const CardInteractionSchema = z.object({
  type: InteractionTypeSchema,
  description: z.string().min(1),
  intensity: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5)]),
  esoteric_note: z.string().min(1),
  decanal_note: z.string().min(1).nullable(),
  angelic_note: z.string().min(1).nullable(),
});

export const CardCombinationSchema = z.object({
  cards: z.tuple([DrawnCardSchema, DrawnCardSchema]),
  interaction: CardInteractionSchema,
});

export const TriadInteractionSchema = z.object({
  type: z.string().min(1),
  description: z.string().min(1),
  pattern: z.enum(['progressive', 'regressive', 'cyclical', 'conflictual', 'harmonic']),
  intensity: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5)]),
});

export const TriadCombinationSchema = z.object({
  cards: z.tuple([DrawnCardSchema, DrawnCardSchema, DrawnCardSchema]),
  interaction: TriadInteractionSchema,
});

export const ElementBalanceSchema = z.object({
  Fuoco: z.number(),
  Acqua: z.number(),
  Aria: z.number(),
  Terra: z.number(),
});

export const SuitDistributionSchema = z.object({
  Bastoni: z.number(),
  Coppe: z.number(),
  Spade: z.number(),
  Denari: z.number(),
});

export const ModalityBalanceSchema = z.object({
  Cardinale: z.number(),
  Fisso: z.number(),
  Mutabile: z.number(),
});

export const FullInterpretationSchema = z.object({
  cards: z.array(CardInterpretationSchema),
  combinations: z.array(CardCombinationSchema),
  triads: z.array(TriadCombinationSchema),
  elemental_balance: ElementBalanceSchema,
  suit_distribution: SuitDistributionSchema,
  major_minor_ratio: z.object({
    major: z.number().int().nonnegative(),
    minor: z.number().int().nonnegative(),
    court: z.number().int().nonnegative(),
  }),
  dominant_suit: SuitSchema.nullable(),
  dominant_element: ElementSchema.nullable(),
  dominant_number: z.number().int().nonnegative().nullable(),
  dominant_sephirah: SephirahNameSchema.nullable(),
  numerological_patterns: z.array(z.string().min(1)),
  zodiacal_patterns: z.array(z.string().min(1)),
  decanal_sequence: z.string().min(1).nullable(),
  modality_balance: ModalityBalanceSchema,
  synthesis: z.string().min(1),
  lilith_narrative: z.string().min(1).nullable(),
  kabbalistic_narrative: z.string().min(1).nullable(),
  qliphotic_narrative: z.string().min(1).nullable(),
  dignity_summary: z.string().min(1).nullable(),
  temporal_range: z.string().min(1).nullable(),
});

export const LLMBackendSchema = z.enum([
  'hosted_mini',
  'external_api',
  'lm_studio_local',
  'symbolic_only',
]);

export const ReadingSchema = z.object({
  id: z.string().min(1),
  user_id: z.string().min(1),
  spread_id: z.string().min(1),
  spread_name: z.string().min(1),
  question: z.string().min(1),
  intent: z.string().min(1).nullable(),
  cards: z.array(DrawnCardSchema),
  interpretation: FullInterpretationSchema,
  entropy_audit: EntropyAuditSchema,
  llm_response: z.string().min(1).nullable(),
  llm_backend: LLMBackendSchema,
  mode: ReadingModeSchema,
  notes: z.string(),
  tags: z.array(z.string().min(1)),
  is_favorite: z.boolean(),
  created_at: z.string().min(1),
  updated_at: z.string().min(1),
});

export const ReadingJsonSchema = z.toJSONSchema(ReadingSchema);
